'use client'

import * as React from 'react'

import { cn } from '../cn'

/**
 * Box drawn over a child iframe + the resulting screenshot crop.
 *
 * The annotator does NOT render the iframe — wrap it around your own
 * `<iframe>` (or any embedded surface). When `enabled` flips to true an
 * absolute-positioned overlay captures pointer events on top of the
 * iframe, the user drags a rectangle, and on mouseup the annotator asks
 * the framed origin for a screenshot via `postMessage`. The framed page
 * must respond with `{ type: 'ordo.screenshotReady', requestId, dataUrl }`
 * — ship that handshake with `@ordo/screenshot-bridge` in each embedded
 * app's client entry.
 *
 * The captured dataUrl is cropped to the drawn box client-side; the
 * caller receives a Blob plus the URL, viewport, and box coords it took.
 */
export interface AnnotationCapture {
  blob: Blob
  /** The URL of the framed page at capture time. */
  url: string
  /** Viewport size used to interpret the box coords. */
  viewport: { width: number; height: number }
  /** Box in iframe-relative client pixels: { x, y, w, h }. */
  box: { x: number; y: number; w: number; h: number }
}

export interface ScreenAnnotatorProps {
  iframeRef: React.RefObject<HTMLIFrameElement | null>
  enabled: boolean
  onCapture: (capture: AnnotationCapture) => void
  onCancel?: () => void
  /** Fired when the screenshot-bridge handshake fails (no response,
   *  timeout, bridge errored). Distinct from onCancel (user pressed
   *  Esc) so the host can show a toast. If omitted, errors fall back
   *  to onCancel — matching the historical behaviour, but losing the
   *  reason; new call sites should wire this through. */
  onError?: (error: Error) => void
  /** Origin regex that incoming postMessage responses must match. Defaults
   *  to "any subdomain of .ordo.com over https". Override in tests or for
   *  alternate root domains. */
  allowedOrigin?: RegExp
  /** How long to wait for the embedded app's screenshot bridge to
   *  respond. 10s — html2canvas on a heavy page can take a few seconds
   *  to rasterize, especially in a preview env that hasn't warmed its
   *  caches. The earlier 3s default was tripping silently on
   *  agent.preview.ordo.com. */
  handshakeTimeoutMs?: number
  className?: string
}

const DEFAULT_ALLOWED_ORIGIN = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)*\.ordo\.com$/i
const DEFAULT_HANDSHAKE_TIMEOUT_MS = 10_000

type Box = { x: number; y: number; w: number; h: number }

const MIN_BOX_SIDE_PX = 8

const ScreenAnnotator = React.forwardRef<HTMLDivElement, ScreenAnnotatorProps>(
  (
    {
      iframeRef,
      enabled,
      onCapture,
      onCancel,
      onError,
      allowedOrigin,
      handshakeTimeoutMs = DEFAULT_HANDSHAKE_TIMEOUT_MS,
      className,
    },
    ref,
  ) => {
    const [dragStart, setDragStart] = React.useState<{ x: number; y: number } | null>(null)
    const [dragEnd, setDragEnd] = React.useState<{ x: number; y: number } | null>(null)
    const overlayRef = React.useRef<HTMLDivElement | null>(null)

    const allowed = allowedOrigin ?? DEFAULT_ALLOWED_ORIGIN

    React.useEffect(() => {
      if (!enabled) {
        setDragStart(null)
        setDragEnd(null)
      }
    }, [enabled])

    React.useEffect(() => {
      if (!enabled) {
        return
      }
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onCancel?.()
        }
      }
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
    }, [enabled, onCancel])

    if (!enabled) {
      return null
    }

    const box = computeBox(dragStart, dragEnd)

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = overlayRef.current?.getBoundingClientRect()
      if (!rect) {
        return
      }
      setDragStart({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      setDragEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragStart) {
        return
      }
      const rect = overlayRef.current?.getBoundingClientRect()
      if (!rect) {
        return
      }
      setDragEnd({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }

    const handleMouseUp = async () => {
      if (!box || box.w < MIN_BOX_SIDE_PX || box.h < MIN_BOX_SIDE_PX) {
        setDragStart(null)
        setDragEnd(null)
        return
      }
      const iframe = iframeRef.current
      const overlayRect = overlayRef.current?.getBoundingClientRect()
      if (!iframe || !iframe.contentWindow || !overlayRect) {
        setDragStart(null)
        setDragEnd(null)
        return
      }
      try {
        const capture = await runHandshake(
          iframe.contentWindow,
          allowed,
          iframe.src,
          box,
          {
            width: Math.round(overlayRect.width),
            height: Math.round(overlayRect.height),
          },
          handshakeTimeoutMs,
        )
        onCapture(capture)
      } catch (err) {
        console.warn('[screen-annotator] capture failed', err)
        const error = err instanceof Error ? err : new Error(String(err))
        if (onError) {
          onError(error)
        } else {
          onCancel?.()
        }
      } finally {
        setDragStart(null)
        setDragEnd(null)
      }
    }

    return (
      <div ref={ref} className={cn('absolute inset-0 z-50', className)}>
        <div
          ref={overlayRef}
          role="presentation"
          className="absolute inset-0 cursor-crosshair bg-black/30 select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
        {box ? (
          <div
            className="pointer-events-none absolute border-2 border-indigo-400 bg-indigo-400/20"
            style={{ left: box.x, top: box.y, width: box.w, height: box.h }}
          />
        ) : null}
        <div className="pointer-events-none absolute left-2 top-2 rounded bg-background/90 px-2 py-1 text-xs shadow">
          Drag to annotate. Esc to cancel.
        </div>
      </div>
    )
  },
)
ScreenAnnotator.displayName = 'ScreenAnnotator'

function computeBox(start: { x: number; y: number } | null, end: { x: number; y: number } | null): Box | null {
  if (!start || !end) {
    return null
  }
  const x = Math.min(start.x, end.x)
  const y = Math.min(start.y, end.y)
  const w = Math.abs(end.x - start.x)
  const h = Math.abs(end.y - start.y)
  return { x, y, w, h }
}

async function runHandshake(
  target: Window,
  allowedOrigin: RegExp,
  iframeUrl: string,
  box: Box,
  viewport: { width: number; height: number },
  timeoutMs: number,
): Promise<AnnotationCapture> {
  const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', handler)
      reject(
        new Error(
          `Screenshot bridge did not respond within ${Math.round(timeoutMs / 1000)}s — the embedded app may not have @ordo/screenshot-bridge mounted, or it may not have finished loading.`,
        ),
      )
    }, timeoutMs)

    function handler(event: MessageEvent) {
      if (!allowedOrigin.test(event.origin)) {
        return
      }
      const data = event.data as { type?: unknown; requestId?: unknown; dataUrl?: unknown; error?: unknown }
      if (data?.type !== 'ordo.screenshotReady' || data.requestId !== requestId) {
        return
      }
      window.clearTimeout(timer)
      window.removeEventListener('message', handler)
      if (typeof data.error === 'string') {
        reject(new Error(data.error))
        return
      }
      if (typeof data.dataUrl !== 'string') {
        reject(new Error('Screenshot bridge returned no dataUrl'))
        return
      }
      resolve(data.dataUrl)
    }
    window.addEventListener('message', handler)
    target.postMessage({ type: 'ordo.requestScreenshot', requestId, viewport }, '*')
  })

  const blob = await cropDataUrl(dataUrl, box)
  return {
    blob,
    url: iframeUrl,
    viewport,
    box,
  }
}

async function cropDataUrl(dataUrl: string, box: Box): Promise<Blob> {
  const img = await loadImage(dataUrl)
  // Source coords are relative to the iframe's reported viewport; assume
  // the bridge captured the full viewport so devicePixelRatio = img.width /
  // viewport.width. If they diverge meaningfully the crop is still usable
  // — the agent gets the user's intent + a high-res-ish image.
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(box.w))
  canvas.height = Math.max(1, Math.round(box.h))
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Canvas 2D context unavailable for crop')
  }
  ctx.drawImage(img, box.x, box.y, box.w, box.h, 0, 0, canvas.width, canvas.height)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => {
      if (!b) {
        reject(new Error('canvas.toBlob returned null'))
        return
      }
      resolve(b)
    }, 'image/png')
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image failed to load'))
    img.src = src
  })
}

export { ScreenAnnotator }
